import React, { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Title, Text, Card, Button } from '@tremor/react'
import { DocumentTextIcon, PlusIcon, TrashIcon, PencilIcon, SparklesIcon } from '@heroicons/react/24/outline'
import { getResearchHypotheses, createResearchHypothesis, updateResearchHypothesis, deleteResearchHypothesis, generateHypothesisAISuggestions, selectResearchHypothesis } from '../../Requests/ResearchHypothesisRequests'
import { sanitizeUserInput } from '../../utils/security'
import { useMyContext } from '../ContextProviders/CreditsContextProvider'
import UpgradedLoader from '../UpgradedLoader'
import ConfirmationModal from '../ConfirmationModal'
import HypothesisFormModal from './HypothesisFormModal'
import HypothesisSetEditModal from './HypothesisSetEditModal'
import HypothesisAIGenerationModal from './HypothesisAIGenerationModal'

function ResearchHypothesesTab({ projectUuid, onNavigateNext }) {
    const { t } = useTranslation()
    const { setCredits } = useMyContext()
    
    // Hypotheses state
    const [hypotheses, setHypotheses] = useState([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)

    // UI state
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [hypothesisToDelete, setHypothesisToDelete] = useState(null)
    const [showCreateModal, setShowCreateModal] = useState(false)
    const [showEditModal, setShowEditModal] = useState(false)
    const [hypothesisToEdit, setHypothesisToEdit] = useState(null)
    const [showSetEditModal, setShowSetEditModal] = useState(false)
    const [hypothesisSetToEdit, setHypothesisSetToEdit] = useState(null)
    const [modalLoading, setModalLoading] = useState(false)
    const [showAIModal, setShowAIModal] = useState(false)
    const [aiLoading, setAILoading] = useState(false)

    const loadHypotheses = useCallback(async () => {
        try {
            setLoading(true)
            setError(null)
            const data = await getResearchHypotheses(projectUuid)
            setHypotheses(data.data || [])
        } catch (err) {
            console.error('Failed to load research hypotheses:', err)
            setError(t('Research.ObjectivosHipoteses.Hypotheses.LoadError'))
        } finally {
            setLoading(false)
        }
    }, [projectUuid, t])

    useEffect(() => {
        loadHypotheses()
    }, [loadHypotheses])

    const handleDeleteClick = (hypothesisUuid) => {
        setHypothesisToDelete(hypothesisUuid)
        setShowDeleteModal(true)
    }

    const handleDeleteConfirm = async () => {
        if (!hypothesisToDelete) return

        try {
            await deleteResearchHypothesis(projectUuid, hypothesisToDelete)
            
            // Update state - remove individual hypothesis or entire hypothesis set
            setHypotheses(prev => 
                prev.filter(item => {
                    // For individual hypothesis, filter by UUID
                    if (item.uuid) {
                        return item.uuid !== hypothesisToDelete
                    }
                    
                    // For hypothesis set, check if any hypothesis in set matches the deleted UUID
                    if (item.type === 'hypothesis_set' && item.hypotheses) {
                        return !item.hypotheses.some(h => h.uuid === hypothesisToDelete)
                    }
                    
                    return true
                })
            )
            
            setShowDeleteModal(false)
            setHypothesisToDelete(null)
        } catch (err) {
            console.error('Failed to delete research hypothesis:', err)
            alert(err.error || t('Research.ObjectivosHipoteses.Hypotheses.DeleteError'))
        }
    }

    const handleCreateManual = () => {
        setShowCreateModal(true)
    }

    const handleGenerateAI = () => {
        setShowAIModal(true)
    }

    const handleAIGenerationSubmit = async (responses, scope, hypothesisType) => {
        setAILoading(true)
        try {
            const response = await generateHypothesisAISuggestions(projectUuid, scope, hypothesisType, responses)
            
            if (response.data) {
                // Add new hypotheses to the list (response.data is now an array of hypotheses)
                setHypotheses(prev => [...response.data, ...prev])
                
                // Update frontend credit balance
                if (response.total_credits_used !== undefined) {
                    // You might need to update this based on how credits are managed
                    setCredits(prev => prev - response.total_credits_used)
                }
                
                setShowAIModal(false)
            }
        } catch (err) {
            console.error('Failed to generate AI hypothesis suggestions:', err)
            throw err
        } finally {
            setAILoading(false)
        }
    }

    const handleSelectHypothesis = async (hypothesisUuid) => {
        try {
            const response = await selectResearchHypothesis(projectUuid, hypothesisUuid)
            if (response.data) {
                // Update local state - handle both individual hypotheses and hypothesis sets
                setHypotheses(prev => 
                    prev.map(item => {
                        // Handle individual hypothesis
                        if (item.uuid) {
                            return {
                                ...item,
                                status: item.uuid === hypothesisUuid ? 'active' : (item.status === 'active' ? 'draft' : item.status)
                            }
                        }
                        
                        // Handle hypothesis set
                        if (item.type === 'hypothesis_set' && item.hypotheses) {
                            // Check if any hypothesis in the set matches the selected UUID
                            const containsSelected = item.hypotheses.some(h => h.uuid === hypothesisUuid)
                            
                            return {
                                ...item,
                                status: containsSelected ? 'active' : (item.status === 'active' ? 'draft' : item.status),
                                hypotheses: item.hypotheses.map(h => ({
                                    ...h,
                                    status: containsSelected ? 'active' : (h.status === 'active' ? 'draft' : h.status)
                                }))
                            }
                        }
                        
                        return item
                    })
                )
                
                // Navigate to next step after selecting a hypothesis
                if (onNavigateNext) {
                    setTimeout(() => onNavigateNext(), 500) // Small delay for UX
                }
            }
        } catch (err) {
            console.error('Failed to select research hypothesis:', err)
            alert(err.error || t('Research.ObjectivosHipoteses.Hypotheses.SelectError'))
        }
    }

    const handleEditHypothesis = (hypothesisUuid) => {
        let hypothesis = null
        
        // First, try to find individual hypothesis
        hypothesis = hypotheses.find(item => item.uuid === hypothesisUuid)
        
        // If not found, search within hypothesis sets
        if (!hypothesis) {
            for (const item of hypotheses) {
                if (item.type === 'hypothesis_set' && item.hypotheses) {
                    hypothesis = item.hypotheses.find(h => h.uuid === hypothesisUuid)
                    if (hypothesis) break
                }
            }
        }
        
        if (hypothesis) {
            setHypothesisToEdit(hypothesis)
            setShowEditModal(true)
        } else {
            console.error('Hypothesis not found for editing:', hypothesisUuid)
        }
    }

    const handleEditHypothesisSet = (hypothesisSet) => {
        setHypothesisSetToEdit(hypothesisSet)
        setShowSetEditModal(true)
    }

    const handleCreateSubmit = async (hypothesisData) => {
        setModalLoading(true)
        try {
            if (hypothesisData.hypotheses) {
                // Creating a hypothesis set (new structure)
                const createPromises = hypothesisData.hypotheses.map(hypothesis => {
                    const hypothesisPayload = {
                        hypothesis_text: hypothesis.hypothesis_text,
                        variables_description: hypothesis.variables_description,
                        reasoning: hypothesisData.reasoning,
                        hypothesis_type: hypothesis.type,
                        scope: hypothesisData.scope,
                        status: hypothesisData.status,
                        type: 'manual'
                    }
                    return createResearchHypothesis(projectUuid, hypothesisPayload)
                })
                
                const responses = await Promise.all(createPromises)
                const newHypotheses = responses.map(response => response.data).filter(Boolean)
                
                if (newHypotheses.length > 0) {
                    // Group the hypotheses into a set
                    const hypothesisSet = {
                        type: 'hypothesis_set',
                        set_id: newHypotheses[0].hypothesis_set_id || `set_${Date.now()}`,
                        status: hypothesisData.status,
                        hypotheses: newHypotheses
                    }
                    
                    setHypotheses(prev => [...prev, hypothesisSet])
                    setShowCreateModal(false)
                }
            } else {
                // Creating individual hypothesis (old structure for editing)
                const response = await createResearchHypothesis(projectUuid, hypothesisData)
                if (response.data) {
                    setHypotheses(prev => [...prev, response.data])
                    setShowCreateModal(false)
                }
            }
        } catch (err) {
            console.error('Failed to create research hypothesis:', err)
            throw err
        } finally {
            setModalLoading(false)
        }
    }

    const handleEditSubmit = async (hypothesisData) => {
        if (!hypothesisToEdit) return
        
        setModalLoading(true)
        try {
            const response = await updateResearchHypothesis(projectUuid, hypothesisToEdit.uuid, hypothesisData)
            if (response.data) {
                setHypotheses(prev => prev.map(item => {
                    // Handle individual hypothesis
                    if (item.uuid === hypothesisToEdit.uuid) {
                        return response.data
                    }
                    
                    // Handle hypothesis set - update the specific hypothesis within the set
                    if (item.type === 'hypothesis_set' && item.hypotheses) {
                        const hasUpdatedHypothesis = item.hypotheses.some(h => h.uuid === hypothesisToEdit.uuid)
                        if (hasUpdatedHypothesis) {
                            return {
                                ...item,
                                hypotheses: item.hypotheses.map(h => 
                                    h.uuid === hypothesisToEdit.uuid ? response.data : h
                                )
                            }
                        }
                    }
                    
                    return item
                }))
                setShowEditModal(false)
                setHypothesisToEdit(null)
            }
        } catch (err) {
            console.error('Failed to update research hypothesis:', err)
            throw err
        } finally {
            setModalLoading(false)
        }
    }

    const handleSetEditSubmit = async (setData) => {
        if (!hypothesisSetToEdit) return
        
        setModalLoading(true)
        try {
            // Update each hypothesis with its specific content and shared set properties
            const updatePromises = hypothesisSetToEdit.hypotheses.map(hypothesis => {
                // Find the corresponding hypothesis data from the form
                const hypothesisData = setData.hypotheses?.find(h => h.type === hypothesis.hypothesis_type)
                
                const updatedData = {
                    hypothesis_text: hypothesisData?.hypothesis_text || hypothesis.hypothesis_text,
                    variables_description: hypothesisData?.variables_description || hypothesis.variables_description,
                    reasoning: setData.reasoning,
                    hypothesis_type: hypothesis.hypothesis_type,
                    scope: setData.scope,
                    status: setData.status,
                    type: 'manual'
                }
                return updateResearchHypothesis(projectUuid, hypothesis.uuid, updatedData)
            })

            await Promise.all(updatePromises)
            
            // Update local state
            setHypotheses(prev => prev.map(item => {
                if (item.type === 'hypothesis_set' && item.set_id === hypothesisSetToEdit.set_id) {
                    return {
                        ...item,
                        status: setData.status,
                        hypotheses: item.hypotheses.map(h => {
                            const hypothesisData = setData.hypotheses?.find(hData => hData.type === h.hypothesis_type)
                            return {
                                ...h,
                                hypothesis_text: hypothesisData?.hypothesis_text || h.hypothesis_text,
                                variables_description: hypothesisData?.variables_description || h.variables_description,
                                reasoning: setData.reasoning,
                                scope: setData.scope,
                                status: setData.status
                            }
                        })
                    }
                }
                return item
            }))
            
            setShowSetEditModal(false)
            setHypothesisSetToEdit(null)
        } catch (err) {
            console.error('Failed to update hypothesis set:', err)
            throw err
        } finally {
            setModalLoading(false)
        }
    }

    const getTypeBadge = (type) => {
        const typeColors = {
            general: 'bg-iavisus-900 text-white',
            specific: 'bg-blue-100 text-blue-800',
            null: 'bg-gray-100 text-gray-800'
        }
        
        return (
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${typeColors[type] || typeColors.general}`}>
                {t(`Research.ObjectivosHipoteses.Hypotheses.Types.${type}`)}
            </span>
        )
    }

    const getStatusBadge = (status) => {
        const statusColors = {
            draft: 'bg-gray-100 text-gray-800',
            active: 'bg-iavisus-900 text-white',
            archived: 'bg-yellow-100 text-yellow-800'
        }
        
        const displayText = status === 'active' ? 'selected' : status;
        
        return (
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[status] || statusColors.draft}`}>
                {t(`Research.ObjectivosHipoteses.Hypotheses.Status.${displayText}`)}
            </span>
        )
    }

    if (loading) {
        return (
            <div className="flex justify-center items-center py-12">
                <UpgradedLoader className="animate-spin" w={8} h={8} bg="bg-iavisus-100" />
            </div>
        )
    }

    return (
        <div data-testid="research-hypotheses-container" className="space-y-6">
            <div>
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-3">
                        <DocumentTextIcon className="h-6 w-6 text-iavisus-100" />
                        <Title className="text-lg font-semibold">
                            {t('Research.ObjectivosHipoteses.Hypotheses.Title')}
                        </Title>
                    </div>
                    <div className="flex space-x-2">
                        <Button
                            data-testid="research-hypotheses-create-btn"
                            size="sm"
                            icon={PlusIcon}
                            onClick={handleCreateManual}
                            className="bg-white border border-iavisus-100 text-iavisus-100 hover:bg-iavisus-50"
                        >
                            {t('Research.ObjectivosHipoteses.Hypotheses.CreateManual')}
                        </Button>
                        <Button
                            data-testid="research-hypotheses-ai-btn"
                            size="sm"
                            icon={SparklesIcon}
                            onClick={handleGenerateAI}
                            className="bg-iavisus-100 text-white hover:bg-iavisus-200"
                        >
                            {t('Research.ObjectivosHipoteses.Hypotheses.GenerateAI')}
                        </Button>
                    </div>
                </div>
                
                <Text className="text-gray-600 mb-6">
                    {t('Research.ObjectivosHipoteses.Hypotheses.Description')}
                </Text>

                {error && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                        <Text className="text-red-600">{error}</Text>
                    </div>
                )}

                {hypotheses.length === 0 ? (
                    <div data-testid="research-hypotheses-empty" className="bg-gray-50 rounded-lg p-8 text-center">
                        <DocumentTextIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                        <Text className="text-gray-500 mb-2">
                            {t('Research.ObjectivosHipoteses.Hypotheses.Description')}
                        </Text>
                        <Text className="text-gray-400 text-sm">
                            Create your first research hypothesis to get started.
                        </Text>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {hypotheses.map((item) => (
                            item.type === 'hypothesis_set' ? (
                                <HypothesisSetCard 
                                    key={item.set_id} 
                                    hypothesisSet={item}
                                    onSelect={handleSelectHypothesis}
                                    onEdit={handleEditHypothesisSet}
                                    onDelete={handleDeleteClick}
                                    t={t}
                                    getTypeBadge={getTypeBadge}
                                    getStatusBadge={getStatusBadge}
                                />
                            ) : (
                                <IndividualHypothesisCard
                                    key={item.uuid}
                                    hypothesis={item}
                                    onSelect={handleSelectHypothesis}
                                    onEdit={handleEditHypothesis}
                                    onDelete={handleDeleteClick}
                                    t={t}
                                    getTypeBadge={getTypeBadge}
                                    getStatusBadge={getStatusBadge}
                                />
                            )
                        ))}
                    </div>
                )}
            </div>

            {/* Create Hypothesis Modal */}
            <HypothesisFormModal
                isOpen={showCreateModal}
                onClose={() => setShowCreateModal(false)}
                onSubmit={handleCreateSubmit}
                loading={modalLoading}
            />

            {/* Edit Hypothesis Modal */}
            <HypothesisFormModal
                isOpen={showEditModal}
                onClose={() => {
                    setShowEditModal(false)
                    setHypothesisToEdit(null)
                }}
                onSubmit={handleEditSubmit}
                editHypothesis={hypothesisToEdit}
                loading={modalLoading}
            />

            {/* Edit Hypothesis Set Modal */}
            <HypothesisSetEditModal
                isOpen={showSetEditModal}
                onClose={() => {
                    setShowSetEditModal(false)
                    setHypothesisSetToEdit(null)
                }}
                onSubmit={handleSetEditSubmit}
                editHypothesisSet={hypothesisSetToEdit}
                loading={modalLoading}
            />

            {/* Delete Confirmation Modal */}
            <ConfirmationModal
                open={showDeleteModal}
                setOpen={setShowDeleteModal}
                title={t('Common.Delete')}
                message={t('Research.ObjectivosHipoteses.Hypotheses.DeleteConfirm')}
                confirmText={t('Common.Delete')}
                cancelText={t('Common.Cancel')}
                variant="danger"
                onConfirm={handleDeleteConfirm}
            />

            {/* AI Hypothesis Generation Modal */}
            <HypothesisAIGenerationModal
                isOpen={showAIModal}
                onClose={() => setShowAIModal(false)}
                onSubmit={handleAIGenerationSubmit}
                loading={aiLoading}
            />
        </div>
    )
}

// Component for rendering a hypothesis set (all three types together)
const HypothesisSetCard = ({ hypothesisSet, onSelect, onEdit, onDelete, t, getTypeBadge, getStatusBadge }) => {
    const { hypotheses } = hypothesisSet
    const generalHypothesis = hypotheses.find(h => h.hypothesis_type === 'general')
    const specificHypothesis = hypotheses.find(h => h.hypothesis_type === 'specific')
    const nullHypothesis = hypotheses.find(h => h.hypothesis_type === 'null')

    return (
        <Card data-testid={`research-hypotheses-set-${hypothesisSet.set_id}`} className="p-4">
            <div className="flex justify-between items-start mb-4">
                <div className="flex items-center space-x-2">
                    <span className="inline-block w-2 h-2 rounded-full bg-iavisus-100"></span>
                    <Text className="text-xs text-gray-500">
                        {t('Research.ObjectivosHipoteses.Hypotheses.AIGenerated')}
                    </Text>
                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                        Hypothesis Set
                    </span>
                    {getStatusBadge(hypothesisSet.status)}
                </div>
                <div className="flex space-x-2">
                    {hypothesisSet.status !== 'active' && (
                        <button
                            onClick={() => onSelect(generalHypothesis?.uuid)}
                            className="px-3 py-1 text-sm bg-iavisus-100 text-white rounded-md hover:bg-iavisus-200 transition-colors"
                            title={t('Research.ObjectivosHipoteses.Hypotheses.SelectHypothesis')}
                        >
                            {t('Research.ObjectivosHipoteses.Hypotheses.Select')}
                        </button>
                    )}
                    <button
                        onClick={() => onEdit(hypothesisSet)}
                        className="p-1 text-gray-500 hover:text-gray-700"
                        title={t('Common.Edit')}
                    >
                        <PencilIcon className="h-4 w-4" />
                    </button>
                    <button
                        onClick={() => onDelete(generalHypothesis?.uuid)}
                        className="p-1 text-red-500 hover:text-red-700"
                        title={t('Common.Delete')}
                    >
                        <TrashIcon className="h-4 w-4" />
                    </button>
                </div>
            </div>

            {/* General Hypothesis (H1) */}
            {generalHypothesis && (
                <div className="mb-3 pb-3 border-b border-gray-100">
                    <div className="flex items-center space-x-2 mb-2">
                        <span className="text-xs font-medium text-gray-600">
                            H1 - General Hypothesis
                        </span>
                    </div>
                    <Text className="text-gray-900 text-sm">{generalHypothesis.hypothesis_text}</Text>
                </div>
            )}

            {/* Specific Hypothesis (H2-Hn) */}
            {specificHypothesis && (
                <div className="mb-3 pb-3 border-b border-gray-100">
                    <div className="flex items-center space-x-2 mb-2">
                        <span className="text-xs font-medium text-gray-600">
                            H2-Hn - Specific Hypothesis
                        </span>
                    </div>
                    <Text className="text-gray-900 text-sm">{specificHypothesis.hypothesis_text}</Text>
                </div>
            )}

            {/* Null Hypothesis (H0) */}
            {nullHypothesis && (
                <div className="mb-3">
                    <div className="flex items-center space-x-2 mb-2">
                        <span className="text-xs font-medium text-gray-600">
                            H0 - Null Hypothesis
                        </span>
                    </div>
                    <Text className="text-gray-900 text-sm">{nullHypothesis.hypothesis_text}</Text>
                </div>
            )}

            {/* Set metadata */}
            <div className="flex items-center justify-between text-xs text-gray-500 pt-3 border-t border-gray-100">
                <div className="flex items-center space-x-4">
                    <span>{t('Research.ObjectivosHipoteses.Hypotheses.CreatedBy')} {generalHypothesis?.user?.name || t('Common.Unknown')}</span>
                    <span>{generalHypothesis && new Date(generalHypothesis.created_at).toLocaleDateString()}</span>
                    <span>3 hypotheses</span>
                </div>
            </div>
        </Card>
    )
}

// Component for rendering an individual hypothesis
const IndividualHypothesisCard = ({ hypothesis, onSelect, onEdit, onDelete, t, getTypeBadge, getStatusBadge }) => {
    return (
        <Card data-testid={`research-hypotheses-item-${hypothesis.uuid}`} className="p-4">
            <div className="flex justify-between items-start">
                <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                        <span className={`inline-block w-2 h-2 rounded-full ${
                            hypothesis.type === 'ai_generated' ? 'bg-iavisus-100' : 'bg-blue-500'
                        }`}></span>
                        <Text className="text-xs text-gray-500">
                            {hypothesis.type === 'ai_generated' ? 
                                t('Research.ObjectivosHipoteses.Hypotheses.AIGenerated') : 
                                t('Research.ObjectivosHipoteses.Hypotheses.ManualType')
                            }
                        </Text>
                        {getTypeBadge(hypothesis.hypothesis_type)}
                        {getStatusBadge(hypothesis.status)}
                    </div>
                    
                    <div className="mb-3">
                        <Text className="font-medium text-gray-900 mb-1">Hypothesis Statement:</Text>
                        <Text className="text-gray-700">
                            {hypothesis.hypothesis_text || t('Research.ObjectivosHipoteses.Hypotheses.EmptyStatement')}
                        </Text>
                    </div>

                    {hypothesis.variables_description && (
                        <div className="mb-3">
                            <Text className="font-medium text-gray-900 mb-1">Variables Description:</Text>
                            <Text className="text-gray-700 text-sm">
                                {hypothesis.variables_description}
                            </Text>
                        </div>
                    )}

                    {hypothesis.reasoning && (
                        <details className="mb-2">
                            <summary className="text-sm text-gray-600 cursor-pointer hover:text-gray-800">
                                {t('Research.ObjectivosHipoteses.Hypotheses.ShowReasoning')}
                            </summary>
                            <Text className="text-sm text-gray-600 mt-2 pl-4 border-l-2 border-gray-200">
                                {hypothesis.reasoning}
                            </Text>
                        </details>
                    )}
                    
                    <div className="flex items-center space-x-4 text-xs text-gray-500">
                        <span>{t('Research.ObjectivosHipoteses.Hypotheses.CreatedBy')} {hypothesis.user?.name || t('Common.Unknown')}</span>
                        <span>{new Date(hypothesis.created_at).toLocaleDateString()}</span>
                        {hypothesis.credits_used > 0 && (
                            <span>{hypothesis.credits_used} {t('Common.Credits')}</span>
                        )}
                    </div>
                </div>
                <div className="flex space-x-2 ml-4">
                    {hypothesis.status !== 'active' && (
                        <button
                            onClick={() => onSelect(hypothesis.uuid)}
                            className="px-3 py-1 text-sm bg-iavisus-100 text-white rounded-md hover:bg-iavisus-200 transition-colors"
                            title={t('Research.ObjectivosHipoteses.Hypotheses.SelectHypothesis')}
                        >
                            {t('Research.ObjectivosHipoteses.Hypotheses.Select')}
                        </button>
                    )}
                    <button
                        onClick={() => onEdit(hypothesis.uuid)}
                        className="p-1 text-gray-500 hover:text-gray-700"
                        title={t('Common.Edit')}
                    >
                        <PencilIcon className="h-4 w-4" />
                    </button>
                    <button
                        onClick={() => onDelete(hypothesis.uuid)}
                        className="p-1 text-red-500 hover:text-red-700"
                        title={t('Common.Delete')}
                    >
                        <TrashIcon className="h-4 w-4" />
                    </button>
                </div>
            </div>
        </Card>
    )
}

export default ResearchHypothesesTab